import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Search, ChevronLeft, ChevronRight, Bug, Lightbulb, ShieldCheck, Eye, Forward } from 'lucide-react';
import { apiService } from '@/core/api/service';
import type { UserReport, UserReportStatus, UserReportType } from '@/core/models';
import { PageHeader } from '@/components/PageHeader';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { useAppState } from '@/core/app-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

export const Route = createFileRoute('/user-reports')({
  head: () => ({
    meta: [{ title: 'Segnalazioni · Replya Internal Backoffice' }],
  }),
  component: UserReportsPage,
});

const LIMIT = 25;

const STATUS_META: Record<UserReportStatus, { label: string; className: string }> = {
  new: { label: 'Nuova', className: 'text-amber-600 border-amber-200 bg-amber-50' },
  triaged: { label: 'Letta', className: 'text-sky-600 border-sky-200 bg-sky-50' },
  planned: { label: 'In roadmap', className: 'text-violet-600 border-violet-200 bg-violet-50' },
  done: { label: 'Rilasciata', className: 'text-emerald-600 border-emerald-200 bg-emerald-50' },
  declined: { label: 'Scartata', className: 'text-slate-500 border-slate-200 bg-slate-50' },
};

const STATUS_ORDER: UserReportStatus[] = ['new', 'triaged', 'planned', 'done', 'declined'];

function formatDateTime(iso?: string) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('it-IT');
}

function TypeBadge({ type }: { type: UserReportType }) {
  if (type === 'bug') {
    return (
      <Badge variant="outline" className="text-xs text-rose-600 border-rose-200 bg-rose-50">
        <Bug className="mr-1 h-3 w-3" />
        Bug
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs text-brand border-brand/30 bg-brand/10">
      <Lightbulb className="mr-1 h-3 w-3" />
      Idea
    </Badge>
  );
}

function StatusBadge({ status }: { status: UserReportStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.new;
  return (
    <Badge variant="outline" className={`text-xs ${meta.className}`}>
      {meta.label}
    </Badge>
  );
}

/** Chi ha segnalato: l'anonimato promesso nell'app vale anche qui. */
function author(report: UserReport): string {
  if (report.anonymous) {
    return report.contactEmail ? `Anonimo · recapito ${report.contactEmail}` : 'Anonimo';
  }
  return report.userEmail || report.contactEmail || report.firebaseId || 'Non identificato';
}

/**
 * Inoltra la segnalazione ai referenti del tenant (Sector Config → Referenti per
 * le segnalazioni), non al team Replya. Due clic: il primo chiede conferma, perché
 * l'email arriva a persone dell'azienda cliente.
 */
function ForwardButton({ report, withLabel = false }: { report: UserReport; withLabel?: boolean }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const mutation = useMutation({
    mutationFn: () => apiService.forwardUserReport(report.tenantId, report.id),
    onSuccess: (data) => {
      toast.success(`Segnalazione inoltrata a ${data.to.join(', ')}`);
      queryClient.invalidateQueries({ queryKey: ['userReports'] });
    },
    onError: (error) => toast.error(`Inoltro non riuscito: ${(error as Error).message}`),
    onSettled: () => setConfirming(false),
  });

  const label = mutation.isPending
    ? 'Invio...'
    : confirming
      ? 'Conferma invio'
      : report.forwardedAt
        ? 'Inoltra di nuovo'
        : 'Inoltra ai referenti';

  return (
    <Button
      size="sm"
      variant={confirming ? 'default' : 'outline'}
      className="rounded-xl"
      disabled={mutation.isPending}
      title={`Inoltra ai referenti di ${report.tenantId} impostati in Sector Config`}
      onClick={() => (confirming ? mutation.mutate() : setConfirming(true))}
      onBlur={() => !mutation.isPending && setConfirming(false)}
    >
      <Forward className="h-4 w-4" />
      {(withLabel || confirming) && <span className="ml-2">{label}</span>}
    </Button>
  );
}

function forwardedLine(report: UserReport): string | null {
  if (!report.forwardedAt) return null;
  return `Inoltrata il ${formatDateTime(report.forwardedAt)}${report.forwardedTo?.length ? ` a ${report.forwardedTo.join(', ')}` : ''}`;
}

function ReportDetailDialog({
  report,
  open,
  onClose,
}: {
  report: UserReport | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: (patch: { status?: UserReportStatus; adminNote?: string }) =>
      apiService.updateUserReport(report!.tenantId, report!.id, patch),
    onSuccess: () => {
      toast.success('Segnalazione aggiornata');
      queryClient.invalidateQueries({ queryKey: ['userReports'] });
      queryClient.invalidateQueries({ queryKey: ['userReportStats'] });
    },
    onError: (error) => toast.error(`Errore: ${(error as Error).message}`),
  });

  if (!report) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl border bg-surface max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Dettaglio segnalazione
            <TypeBadge type={report.type} />
            <StatusBadge status={report.status} />
          </DialogTitle>
          <DialogDescription>
            {formatDateTime(report.createdAt)} · {report.tenantId}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Messaggio</Label>
            <div className="rounded-xl border border-border bg-surface-2 p-3 text-sm whitespace-pre-wrap">
              {report.message}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Da</Label>
              <p className="text-sm flex items-center gap-1.5">
                {report.anonymous && <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />}
                {author(report)}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Contesto</Label>
              <p className="text-sm">
                v{report.appVersion || 'n/d'} · {report.path || 'n/d'} · {report.locale || 'n/d'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface-2 p-3">
            <p className="text-xs text-muted-foreground">
              {forwardedLine(report) ?? 'Non ancora inoltrata ai referenti del tenant.'}
            </p>
            <ForwardButton report={report} withLabel />
          </div>

          {report.userAgent && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Browser</Label>
              <p className="text-xs font-mono break-all text-muted-foreground">{report.userAgent}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Stato</Label>
            <div className="flex flex-wrap gap-2">
              {STATUS_ORDER.map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={report.status === status ? 'default' : 'outline'}
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ status })}
                  className="rounded-xl"
                >
                  {STATUS_META[status].label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Nota interna</Label>
            {report.adminNote && (
              <p className="rounded-xl border border-border bg-surface-2 p-3 text-sm whitespace-pre-wrap">
                {report.adminNote}
              </p>
            )}
            <div className="flex gap-2">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Aggiungi una nota (sostituisce la precedente)"
                className="rounded-xl bg-surface-2 border-border h-9"
              />
              <Button
                disabled={mutation.isPending || !note.trim()}
                onClick={() => mutation.mutate({ adminNote: note.trim() })}
                className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground h-9"
              >
                Salva
              </Button>
            </div>
            {report.reviewedAt && (
              <p className="text-xs text-muted-foreground">
                Ultima revisione: {formatDateTime(report.reviewedAt)}
                {report.reviewedBy ? ` · ${report.reviewedBy}` : ''}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UserReportsPage() {
  // Le segnalazioni sono del tenant selezionato in alto, come le altre pagine.
  const { selectedTenant } = useAppState();
  const tenantId = selectedTenant?.tenantId;
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<UserReport | null>(null);
  const [filters, setFilters] = useState({
    status: 'all' as UserReportStatus | 'all',
    type: 'all' as UserReportType | 'all',
    search: '',
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['userReports', tenantId, filters, offset],
    enabled: !!tenantId,
    queryFn: () =>
      apiService.listUserReports({
        tenantId,
        limit: LIMIT,
        offset,
        status: filters.status,
        type: filters.type,
        search: filters.search || undefined,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ['userReportStats', tenantId],
    queryFn: () => apiService.getUserReportStats(tenantId),
    enabled: !!tenantId,
  });

  const total = data?.total ?? 0;
  const hasNext = offset + LIMIT < total;
  const hasPrev = offset > 0;

  const applyFilters = () => {
    setOffset(0);
    refetch();
  };

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Segnalazioni"
        subtitle={`Idee e bug inviati dagli utenti di ${selectedTenant.name} dall'app`}
      />

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {STATUS_ORDER.map((status) => (
          <Card key={status} className="rounded-2xl border bg-surface">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{STATUS_META[status].label}</p>
              <p className="text-2xl font-bold tabular-nums">{stats?.byStatus[status] ?? 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl border bg-surface">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Stato</Label>
              <Select
                value={filters.status}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, status: v as typeof filters.status }))
                }
              >
                <SelectTrigger className="rounded-xl bg-surface-2 border-border h-9 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {STATUS_ORDER.map((status) => (
                    <SelectItem key={status} value={status}>
                      {STATUS_META[status].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tipo</Label>
              <Select
                value={filters.type}
                onValueChange={(v) => setFilters((f) => ({ ...f, type: v as typeof filters.type }))}
              >
                <SelectTrigger className="rounded-xl bg-surface-2 border-border h-9 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="feature">Idea</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 min-w-[200px]">
              <Label className="text-xs">Cerca nel testo</Label>
              <Input
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="parola chiave"
                className="rounded-xl bg-surface-2 border-border h-9"
              />
            </div>
            <Button
              onClick={applyFilters}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground h-9"
            >
              <Search className="mr-2 h-4 w-4" />
              Filtra
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle className="text-base">
            Risultati{' '}
            {data ? `(${total === 0 ? 0 : offset + 1}-${Math.min(offset + LIMIT, total)} di ${total})` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento...</p>
          ) : isError ? (
            <p className="text-sm text-destructive">Errore: {(error as Error).message}</p>
          ) : total === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna segnalazione con questi filtri.</p>
          ) : (
            <>
              <div className="overflow-x-auto -mx-2 px-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Messaggio</TableHead>
                      <TableHead>Da</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.items.map((report) => (
                      <TableRow key={`${report.tenantId}-${report.id}`}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {formatDateTime(report.createdAt)}
                        </TableCell>
                        <TableCell>
                          <TypeBadge type={report.type} />
                        </TableCell>
                        <TableCell className="max-w-md">
                          <p className="text-sm line-clamp-2">{report.message}</p>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {author(report)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={report.status} />
                          {report.forwardedAt && (
                            <p className="mt-1 text-[11px] text-muted-foreground" title={forwardedLine(report) ?? ''}>
                              Inoltrata
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                          <ForwardButton report={report} />
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl"
                            onClick={() => setSelected(report)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={!hasPrev}
                  onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Precedenti
                </Button>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={!hasNext}
                  onClick={() => setOffset((o) => o + LIMIT)}
                >
                  Successive
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ReportDetailDialog
        // Dalla lista aggiornata: dopo un inoltro o un cambio di stato il dettaglio aperto si aggiorna.
        report={data?.items.find((r) => r.id === selected?.id && r.tenantId === selected?.tenantId) ?? selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
